import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import * as _ from 'lodash';

interface ParseOptions {
  // SPECIFY ANY VALUES FOR WHOSE KEYS WILL BE DELETED
  excludedValues?: any[];
  // SPECIFY ANY KEYS THAT SHOULD HAVE DATE VALUES
  parseDates?: string[];
  // CONVERT "true" to true
  convertBooleans?: true;
  encode?: boolean;
  decode?: boolean;
}

interface StringifyOptions {
  // SPECIFY ANY VALUES FOR WHOSE PARAMS WILL BE OMITTED
  excludedValues?: any[];
  // SPECIFY ANY KEYS THAT HAVE DATE VALUES
  stringifyDates?: boolean;
}

interface ConfigOptions<T extends object> {
  debouncedValues: (keyof T)[];
  /**
   * If a clearValueHandler is provided, it will be used to clear the value of the default form data.
   * This is only used when there are query params set.
   * This is NOT the default value for the form data.
   */
  clearValueHandlers?: Partial<Record<keyof T, any>>;
}

type MutationOptions<T extends object> = ParseOptions & StringifyOptions & ConfigOptions<T>;

export interface UseSearchParamsOptions<T extends object> {
  parse?: ParseOptions;
  stringify?: StringifyOptions;
  config?: ConfigOptions<T>;
}

export default function useSearchParams<T extends object>(defaultFormData: T, options: UseSearchParamsOptions<T>) {
  const { replace } = useHistory();
  const { search } = useLocation();

  const formState = useMemo(() => {
    if (search && search.length > 0) {
      // If we have query params, we need to clear the default form data values
      const cleared = _.mapValues(defaultFormData, (value, key) => {
        // If a clearValueHandler is provided, use it to clear the value
        const handlers = options.config?.clearValueHandlers;
        if (handlers && key in handlers) {
          return handlers[key as keyof T];
        }
        if (value === null || value === undefined) return value;
        if (dayjs.isDayjs(value)) return null;
        if (typeof value === 'string') return '';
        if (typeof value === 'object') return null;

        return null;
      });
      return { ...cleared, ...(safelyParseParams<T>(search, options.parse) as T) };
    } else {
      return defaultFormData;
    }
  }, [defaultFormData, search, options.parse]);

  const [displayState, setDisplayState] = useState(formState);

  // Merge display state with query string params, prioritizing query string values.
  // Display state is first stringified then parsed to normalize it (handling nulls,
  // dates, etc.) to match the format of query string params. This ensures null values
  // from display state are preserved in queryState, preventing fallback to default
  // page state when query params are absent.
  const queryState = useMemo(
    () => ({
      // display state
      ...safelyParseParams<T>(safelyStringifyParams(displayState, options.stringify), options.parse),
      // state from query string (overrides display state where present)
      ...safelyParseParams<T>(search, options.parse)
    }),
    [displayState, options.stringify, options.parse, search]
  );

  const setSearchParams = useCallback(
    (data: Partial<T>) => {
      const stringifiedParams = safelyStringifyParams(data, options.stringify);
      replace({ search: stringifiedParams });
    },
    [options.stringify, replace]
  );

  const throttledSetSearchParams = useMemo(
    () =>
      _.throttle(
        (data: T) => {
          setSearchParams(data);
        },
        1000,
        { leading: false }
      ),
    [setSearchParams]
  );

  const onFormInput = useCallback(
    (update: Partial<T>) => {
      const partitionToDebounce = _.pick(update, options.config?.debouncedValues || []);
      const partitionToForward = _.omit(update, options.config?.debouncedValues || []);
      setDisplayState((state) => ({ ...state, ...update }));
      // Only throttle the update if it contains values to debounce
      if (!_.isEmpty(partitionToDebounce)) {
        throttledSetSearchParams({ ...displayState, ...partitionToForward, ...partitionToDebounce });
      }
      // Only immediately update if it contains values to not debounce
      if (!_.isEmpty(partitionToForward)) {
        setSearchParams({ ...displayState, ...partitionToForward });
      }
    },
    [displayState, options.config?.debouncedValues, setSearchParams, throttledSetSearchParams]
  );

  return [displayState, queryState, onFormInput] as const;
}

const getObjKeys = Object.keys as <T extends object>(obj: T) => Array<keyof T>;

function mutateObject<T extends object>(obj: T, options?: Partial<MutationOptions<T>>): Partial<T> {
  const validObj = getObjKeys(obj).reduce((acc, key) => {
    let value: any = obj[key];
    if (value && typeof value === 'object' && typeof value?.month !== 'function') {
      return {
        ...acc,
        [key]: mutateObject(value, options)
      };
    }
    if (options?.convertBooleans && ['true', 'false'].includes(value)) {
      // eslint-disable-next-line prettier/prettier
      value = value === 'true';
    }
    if (options?.stringifyDates && typeof value?.month === 'function') {
      value = value.toISOString();
    }
    if (options?.parseDates && options.parseDates.includes(key as string)) {
      value = (value && dayjs(value)) || undefined;
    }
    if (options?.encode && typeof value === 'string') {
      value = encodeURIComponent(value);
    }
    if (options?.decode && typeof value === 'string') {
      value = decodeURIComponent(value);
    }
    return value
      ? {
          ...acc,
          [key]: value
        }
      : acc;
  }, {});
  return validObj;
}

function safelyParseParams<T extends object>(search: string, options?: ParseOptions) {
  const parsedParams = qs.parse(search, { ignoreQueryPrefix: true }) as T;
  return mutateObject(parsedParams, { ...options, decode: true });
}

function safelyStringifyParams(formData: object, options?: StringifyOptions) {
  const safeFormData = mutateObject(formData, { ...options, encode: true });
  return qs.stringify(safeFormData, { encode: false });
}
